/*
 * FloatingControlBar
 * Botonera flotante profesional con soporte sticky, drag vertical, y tooltips inteligentes
 *
 * Características:
 * - Estados visuales hover/active con Material theme tokens
 * - Tooltips con placement dinámico (izquierda → derecha)
 * - Sticky left/right con drag vertical limitado
 * - Rendimiento optimizado (event delegation)
 * - Compatible con fullscreen, resize, y distintos DPI
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  ListItemText,
  Typography,
  useTheme,
  alpha,
  Paper,
} from '@mui/material';
import KeyboardIcon from '@mui/icons-material/Keyboard';

export interface FloatingControlItem {
  id: string;
  icon: React.ReactNode;
  title: string;
  onClick: () => void;
  disabled?: boolean;
  ariaLabel?: string;
  /** Si se proporciona, el botón mostrará un menú desplegable con estas acciones */
  menuItems?: Array<{
    id: string;
    title: string;
    shortcut?: string; // texto que representa el atajo, p.ej. "Alt+F4"
    onClick: () => void;
    disabled?: boolean;
  }>;
}

interface FloatingControlBarProps {
  items: FloatingControlItem[];
  /** Posición inicial: 'top-right' | 'top-left' | 'bottom-left' | 'bottom-right'. Default: 'top-right' */
  position?: 'top-right' | 'top-left' | 'bottom-left' | 'bottom-right';
  /** ZIndex de la barra. Default: 1200 */
  zIndex?: number;
  /** Offset horizontal desde el borde. Default: 16 */
  horizontalOffset?: number;
  /** Offset vertical inicial. Default: 16 */
  verticalOffset?: number;
  /** Opcional: referencia al contenedor (p.ej. vista del agente) para limitar la barra dentro de ese view */
  anchorRef?: React.RefObject<HTMLElement | null>;
  /** Container para renderizar tooltips (crítico en fullscreen) */
  tooltipContainer?: HTMLElement | null;
}

const CONTROL_WIDTH = 44;
const CONTROL_HEIGHT = 40;

/**
 * Componente individual del botón flotante
 * Maneja estados visuales, tooltips, y accesibilidad
 */
const FloatingControlButton: React.FC<{
  item: FloatingControlItem;
  barPosition: 'left' | 'right';
  tooltipContainer?: HTMLElement | null;
}> = ({ item, barPosition, tooltipContainer }) => {
  const theme = useTheme();
  const [isPressed, setIsPressed] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const isDanger = item.id === 'stop-session';

  // Tooltip aparece del lado opuesto al que está pegada la barra
  const tooltipPlacement = barPosition === 'right' ? 'left' : 'right';

  const handleMouseDown = useCallback(() => {
    setIsPressed(true);
  }, []);

  const handleMouseUp = useCallback(() => {
    setIsPressed(false);
  }, []);

  return (
    <Tooltip
      title={item.title}
      placement={tooltipPlacement}
      enterDelay={300}
      leaveDelay={200}
      slotProps={{
        popper: {
          container: tooltipContainer || undefined,
          modifiers: [
            { name: 'offset', options: { offset: [0, 8] } },
            { name: 'preventOverflow', options: { boundary: 'clippingParents', padding: 8 } },
            {
              name: 'flip',
              options: { fallbackPlacements: [tooltipPlacement === 'left' ? 'right' : 'left'] },
            },
          ],
          sx: {
            zIndex: 10000, // Asegurar que aparezca sobre fullscreen
          },
        },
        tooltip: {
          sx: {
            fontSize: '0.75rem',
            fontWeight: 500,
            backgroundColor: theme.palette.grey[900],
            color: theme.palette.common.white,
            boxShadow: theme.shadows[8],
            padding: '4px 8px',
            borderRadius: 1,
          },
        },
      }}
    >
      <span>
        <IconButton
          ref={buttonRef}
          size="small"
          onClick={() => {
            try {
              console.debug(`[FloatingControlBar] click: ${item.id}`);
              item.onClick?.();
            } catch (_e) {
              console.error('[FloatingControlBar] onClick handler error', _e);
            }
          }}
          disabled={item.disabled}
          onMouseDown={handleMouseDown}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          aria-label={item.ariaLabel || item.title}
          sx={{
            width: CONTROL_WIDTH,
            height: CONTROL_HEIGHT,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            transition: theme.transitions.create(['background-color', 'box-shadow'], {
              duration: theme.transitions.duration.shorter,
              easing: theme.transitions.easing.easeInOut,
            }),
            // Estado normal: sutil fondo oscuro
            backgroundColor: alpha(theme.palette.common.black, 0.4),
            color: theme.palette.common.white,
            // Hover: color primary con transición
            '&:hover:not(:disabled)': isDanger
              ? { backgroundColor: theme.palette.error.dark, boxShadow: theme.shadows[4] }
              : { backgroundColor: theme.palette.primary.main, boxShadow: theme.shadows[4] },
            // Active / Press: primario más claro (usar token light cuando exista)
            ...(isPressed &&
              (isDanger
                ? {
                    backgroundColor: theme.palette.error.main,
                    boxShadow: theme.shadows[2],
                  }
                : {
                    backgroundColor: alpha(
                      theme.palette.primary.light || theme.palette.primary.main,
                      0.9
                    ),
                    boxShadow: theme.shadows[2],
                  })),
            // Estado disabled
            '&:disabled': {
              opacity: 0.5,
              cursor: 'not-allowed',
            },
            // Focus ring accesible
            '&:focus-visible': {
              outline: `2px solid ${theme.palette.primary.main}`,
              outlineOffset: 2,
            },
          }}
        >
          {item.icon}
        </IconButton>
      </span>
    </Tooltip>
  );
};

const FloatingControlMenuButton: React.FC<{
  item: FloatingControlItem;
  barPosition: 'left' | 'right';
  tooltipContainer?: HTMLElement | null;
}> = ({ item, barPosition, tooltipContainer }) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleOpen = (ev: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(ev.currentTarget);
  };
  const handleClose = () => setAnchorEl(null);

  return (
    <>
      <Tooltip
        title={item.title}
        placement={barPosition === 'right' ? 'left' : 'right'}
        enterDelay={300}
        leaveDelay={200}
        slotProps={{
          popper: { container: tooltipContainer || undefined },
          tooltip: { sx: { fontSize: '0.75rem' } },
        }}
      >
        <span>
          <IconButton
            size="small"
            onClick={handleOpen}
            disabled={item.disabled}
            aria-haspopup="true"
            aria-expanded={open ? 'true' : undefined}
            aria-label={item.ariaLabel || item.title}
            sx={{
              width: CONTROL_WIDTH,
              height: CONTROL_HEIGHT,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: alpha(theme.palette.common.black, 0.4),
              color: theme.palette.common.white,
              '&:hover:not(:disabled)': {
                backgroundColor: theme.palette.primary.main,
              },
            }}
          >
            {item.icon || <KeyboardIcon fontSize="small" />}
          </IconButton>
        </span>
      </Tooltip>

      {/**
       * Ajustar origen del Menu según la posición de la barra:
       * - Si la barra está a la derecha, abrir a la izquierda del botón
       * - Si la barra está a la izquierda, abrir a la derecha del botón
       * Además agregamos modificadores de Popper (offset, preventOverflow, flip)
       * para evitar que el menú se superponga con otros botones.
       */}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={
          barPosition === 'right'
            ? ({ vertical: 'center', horizontal: 'left' } as any)
            : ({ vertical: 'center', horizontal: 'right' } as any)
        }
        transformOrigin={
          barPosition === 'right'
            ? ({ vertical: 'center', horizontal: 'right' } as any)
            : ({ vertical: 'center', horizontal: 'left' } as any)
        }
        PaperProps={{
          sx: {
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
            minWidth: 200,
            boxShadow: theme.shadows[8],
          },
        }}
      >
        {(item.menuItems || []).map((mi) => (
          <MenuItem
            key={mi.id}
            onClick={() => {
              try {
                mi.onClick?.();
              } catch (_e) {
                console.error('[FloatingControlBar] menu item error', _e);
              }
              handleClose();
            }}
            disabled={mi.disabled}
          >
            <ListItemText primary={mi.title} />
            {mi.shortcut ? (
              <Typography variant="body2" sx={{ opacity: 0.7, ml: 1 }}>
                {mi.shortcut}
              </Typography>
            ) : null}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

import SwapHorizIcon from '@mui/icons-material/SwapHoriz';

/** FloatingControlBar
 * Botonera flotante en 4 esquinas con botón interno para cambiar posición
 */
export const FloatingControlBar: React.FC<FloatingControlBarProps> = ({
  items,
  position = 'top-right',
  zIndex = 1200,
  horizontalOffset = 16,
  verticalOffset = 16,
  anchorRef,
  tooltipContainer,
}) => {
  const theme = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [currentPosition, setCurrentPosition] = useState<
    'top-right' | 'bottom-right' | 'bottom-left' | 'top-left'
  >(position);
  const [verticalPos] = useState<number>(verticalOffset);

  const POSITIONS: Array<'top-right' | 'bottom-right' | 'bottom-left' | 'top-left'> = [
    'top-right',
    'bottom-right',
    'bottom-left',
    'top-left',
  ];

  const cyclePosition = useCallback(() => {
    const idx = POSITIONS.indexOf(currentPosition);
    const next = POSITIONS[(idx + 1) % POSITIONS.length] ?? 'top-right';
    setCurrentPosition(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPosition]);

  useEffect(() => {
    const onResize = () => {
      // placeholder for possible future adjustments
      if (!contentRef.current || !containerRef.current) return;
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return (
    <Box
      ref={containerRef}
      sx={{
        pointerEvents: 'none',
        position: anchorRef ? 'absolute' : 'fixed',
        inset: 0,
        zIndex,
      }}
    >
      <Paper
        ref={contentRef}
        elevation={4}
        sx={{
          pointerEvents: 'auto',
          p: 1,
          backgroundColor: alpha(theme.palette.grey[900], 0.95),
          backdropFilter: 'blur(4px)',
          borderRadius: 2,
          border: `1px solid ${alpha(theme.palette.common.white, 0.1)}`,
          transition: theme.transitions.create(['box-shadow'], {
            duration: theme.transitions.duration.shorter,
          }),
          userSelect: 'none',
          WebkitUserSelect: 'none',
          maxHeight: 'calc(100vh - 32px)',
          overflowY: 'auto',
          overflowX: 'hidden',
          '&::-webkit-scrollbar': {
            width: 4,
          },
          '&::-webkit-scrollbar-track': {
            background: alpha(theme.palette.common.white, 0.05),
          },
          '&::-webkit-scrollbar-thumb': {
            background: alpha(theme.palette.common.white, 0.2),
            borderRadius: 2,
            '&:hover': {
              background: alpha(theme.palette.common.white, 0.3),
            },
          },
          // posicionamiento por esquinas (relativo al contenedor si se proporciona)
          position: anchorRef ? 'absolute' : 'fixed',
          top: currentPosition.startsWith('top') ? verticalPos : 'auto',
          bottom: currentPosition.startsWith('bottom') ? verticalPos : 'auto',
          right: currentPosition.endsWith('right') ? Math.max(0, horizontalOffset) : 'auto',
          left: currentPosition.endsWith('left') ? Math.max(0, horizontalOffset) : 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <IconButton
            size="small"
            onClick={cyclePosition}
            aria-label="Cambiar posición"
            sx={{
              width: 32,
              height: 32,
              color: theme.palette.common.white,
              backgroundColor: alpha(theme.palette.common.black, 0.35),
              '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.9) },
            }}
          >
            <SwapHorizIcon fontSize="small" />
          </IconButton>
        </Box>

        {items.map((item) =>
          item.menuItems && item.menuItems.length > 0 ? (
            <FloatingControlMenuButton
              key={item.id}
              item={item}
              barPosition={currentPosition.endsWith('right') ? 'right' : 'left'}
              tooltipContainer={tooltipContainer}
            />
          ) : (
            <FloatingControlButton
              key={item.id}
              item={item}
              barPosition={currentPosition.endsWith('right') ? 'right' : 'left'}
              tooltipContainer={tooltipContainer}
            />
          )
        )}
      </Paper>
    </Box>
  );
};
